import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { CreditorCaseSummary } from '../interfaces/creditorCaseSummary.interface';
import { CreditorCaseDetailed } from '../interfaces/creditorCaseDetailed.interface';
import { DetailedProofOfDebtResponse } from '../interfaces/creditorDetailedProofOfDebtResponse.interface';
import { DetailedProofOfDebtRequest } from '../interfaces/creditorDetailedProofOfDebtRequest.interface';
import { CreditorPreferentialRight } from '../interfaces/creditorPreferentialRight.interface';
import { CreditorInfo } from '../interfaces/creditorInfo.interface';
import { StoredFile } from '../interfaces/storedfile.interface';
import { CreditorStoredFile } from '../interfaces/creditorStoredFile.interface';
import { CreditorGroupInfo } from '../interfaces/creditorGroupInfo.interface';
import { CreditorGroupInvitationDetails } from '../interfaces/creditorGroupInvitationDetails.interface';
import { CreditorGroupAuditLogEntry } from '../interfaces/creditorGroupAuditLogEntry.interface';
import { CreditorClaimProofOfDebtSummary } from '../interfaces/creditorClaimProofOfDebtSummary.interface';
import { AuditLogPage } from '../interfaces/auditLogPage.interface';
import { CreditorLastSubmitted } from '../interfaces/creditorLastSubmitted.interface';
import { UploadProofOfDebtResult } from '../interfaces/uploadProofOfDebtResult.interface';

@Injectable()
export class CreditorService {
  constructor(
    private http: HttpClient,
    @Inject('API_URL') private apiUrl: string
  ) { }

  public searchCreditorCases(query: string): Observable<CreditorCaseSummary[]> {
    return this.http.get<CreditorCaseSummary[]>(`${this.apiUrl}/Creditor/SearchCases`, { params: { query } });
  }

  public getMyCreditorCases(): Observable<CreditorInfo[]> {
    return this.http.get<CreditorInfo[]>(`${this.apiUrl}/Creditor/CreditorCases`);
  }

  public getDetailedCreditorCase(caseId: string): Observable<CreditorCaseDetailed> {
    return this.http.get<CreditorCaseDetailed>(`${this.apiUrl}/Creditor/DetailedCreditorCase/${caseId}`);
  }

  public getMyClaimsForCase(caseId: string): Observable<CreditorClaimProofOfDebtSummary[]> {
    return this.http.get<CreditorClaimProofOfDebtSummary[]>(`${this.apiUrl}/Creditor/MyClaimsForCase/${caseId}`);
  }

  public getDetailedProofOfDebt(caseId: string, proofOfDebtId: string, ownerCreditorId: string): Observable<DetailedProofOfDebtResponse> {
    return this.http.get<DetailedProofOfDebtResponse>(`${this.apiUrl}/Creditor/GetDetailedProofOfDebt/${caseId}/${proofOfDebtId}/${ownerCreditorId}`);
  }

  public uploadProofOfDebt(caseId: string, payload: DetailedProofOfDebtRequest, files: File[], caseSummary?: CreditorCaseSummary | null): Observable<UploadProofOfDebtResult> {
    const formData = new FormData();

    formData.append('caseId', caseId);
    formData.append('proofOfDebt', JSON.stringify(payload));

    if (caseSummary) {
      formData.append('caseSummary', JSON.stringify(caseSummary));
    }

    for (const file of files) {
      formData.append('files', file, file.name);
    }

    return this.http.post<UploadProofOfDebtResult>(`${this.apiUrl}/Creditor/UploadProofOfDebt`, formData).pipe(
      map(result => {
        if (!result?.claimId) {
          throw new Error('UploadProofOfDebt returned an empty claim id');
        }
        return result;
      })
    );
  }

  public getPreferentialRights(): Observable<CreditorPreferentialRight[]> {
    return this.http.get<CreditorPreferentialRight[]>(`${this.apiUrl}/Creditor/GetPreferentialRights`);
  }

  public getPublishedFiles(caseId: string): Observable<CreditorStoredFile[]> {
    return this.http.get<CreditorStoredFile[]>(`${this.apiUrl}/Creditor/PublishedFiles/${caseId}`);
  }

  public getMyGroup(): Observable<CreditorGroupInfo> {
    return this.http.get<CreditorGroupInfo>(`${this.apiUrl}/CreditorGroup/my-group`);
  }

  public sendGroupInvitation(email: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/CreditorGroup/invite`, { email });
  }

  public revokeGroupInvitation(invitationId: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}/CreditorGroup/invitation/${invitationId}`);
  }

  public removeGroupMember(userId: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}/CreditorGroup/member/${userId}`);
  }

  public getGroupInvitationDetails(token: string): Observable<CreditorGroupInvitationDetails> {
    return this.http.get<CreditorGroupInvitationDetails>(
      `${this.apiUrl}/CreditorGroup/invitation/${token}`
    );
  }

  public acceptGroupInvitation(token: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/CreditorGroup/invitation/${token}/accept`, {});
  }

  public getMyCasesForTransfer(): Observable<CreditorInfo[]> {
    return this.http.get<CreditorInfo[]>(`${this.apiUrl}/CreditorGroup/my-transferable-cases`);
  }

  public transferCases(transfers: { gkCaseId: string; toUserId: string }[]): Observable<any> {
    return this.http.post(`${this.apiUrl}/CreditorGroup/transfer-case`, { transfers });
  }

  public getGroupAuditLog(action?: string, search?: string, take = 20, beforeUtc?: string, beforeId?: string, snapshotMaxUtc?: string): Observable<AuditLogPage> {
    const params = new URLSearchParams({ take: take.toString() });
    if (action) params.set('action', action);
    if (search) params.set('search', search);
    if (beforeUtc) params.set('beforeUtc', beforeUtc);
    if (beforeId) params.set('beforeId', beforeId);
    if (snapshotMaxUtc) params.set('snapshotMaxUtc', snapshotMaxUtc);
    return this.http.get<AuditLogPage>(`${this.apiUrl}/CreditorGroup/audit-log?${params}`);
  }

  public getCreditorLastSubmitted(): Observable<CreditorLastSubmitted | null> {
    return this.http.get<CreditorLastSubmitted | null>(`${this.apiUrl}/Creditor/LastSubmitted`);
  }

  public getProofOfDebtReceipt(
    caseId: string,
    ownerCreditorId: string,
    claimId: string,
    language: string,
    isPreRequest: boolean,
    caseName: string | undefined,
    submittedDate: string | undefined
  ): Observable<{ blob: Blob; filename: string }> {
    const filename = this.buildReceiptFilename(isPreRequest, caseName, submittedDate);
    return this.http.get(
      `${this.apiUrl}/Creditor/ProofOfDebtReceipt/${caseId}/${ownerCreditorId}/${claimId}`,
      { params: { language }, responseType: 'blob' }
    ).pipe(
      map(blob => ({ blob, filename }))
    );
  }

  private buildReceiptFilename(isPreRequest: boolean, caseName: string | undefined, submittedDate: string | undefined): string {
    const claimType = isPreRequest ? 'Anmald_fordran' : 'Bevakad_fordran';
    const sanitizedCaseName = this.sanitizeCaseNameForFilename(caseName) || 'okand';
    const formattedDate = this.formatDateForFilename(submittedDate) || new Date().toISOString().slice(0, 10);
    return `${claimType}_${sanitizedCaseName}_${formattedDate}.pdf`;
  }

  private sanitizeCaseNameForFilename(input: string | undefined | null): string {
    if (!input?.trim()) return '';
    let result = '';
    for (const char of input.trim().replace(/[åÅ]/g, 'a').replace(/[äÄ]/g, 'a').replace(/[öÖ]/g, 'o')) {
      if (/[a-zA-Z0-9]/.test(char)) result += char;
      else if (char === ' ' || char === '_' || char === '-') result += '_';
    }
    return result.replace(/^_+|_+$/g, '');
  }

  private formatDateForFilename(dateStr: string | undefined | null): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime()) || date.getFullYear() < 1900) return '';
    return date.toISOString().slice(0, 10);
  }

  public getPublishedFileStream(fileId: string, caseId: string, isSharepointFile: boolean, ownerCreditorId: string): Observable<Blob> {
    if (isSharepointFile) {
      return this.http.get(
        `${this.apiUrl}/Creditor/GetSharepointFile/${fileId}/${caseId}/${ownerCreditorId}`,
        { responseType: 'blob' }
      );
    }
    else {
      return this.http.get(
        `${this.apiUrl}/Creditor/GetFile/${fileId}/${caseId}/${ownerCreditorId}`,
        { responseType: 'blob' }
      );
    }
  }
}
